import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import {
  demoEmail,
  matchesSearchEndpoint,
  type DemoFixtureIdentity,
  type PslfDemoScenarioId,
} from 'tio-employee/demo/types';
import { PSLF_GROUP_ID } from 'tio-employee/demo/identity-fixtures';

// Stable ids let the handler rewrite requested employee/person ids while the
// sideloaded PSLF graph and identity-derived borrower names stay coherent.

const FEIN = '12-3456789';

function buildEmployeeDoc(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): JsonApiDoc {
  const { firstName, lastName, employerName } = identity;
  const forms = buildPslfFormResources(scenarioId, identity);
  const formRefs = forms.map(({ type, id }) => ({ type, id }));
  return {
    data: {
      type: 'employee',
      id: 'demo-employee-1',
      attributes: {
        firstName,
        lastName,
        email: demoEmail(identity),
        isActive: true,
        title: 'Registered Nurse',
        employmentStartDate: '2022-01-15',
        customAttributes: { fein: FEIN },
      },
      relationships: {
        pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
        company: { data: { type: 'company', id: 'demo-company-1' } },
        employeeAddition: { data: { type: 'employee-addition', id: 'demo-emp-add-1' } },
        pslfForms: { data: formRefs },
      },
    },
    included: [
      {
        type: 'pslf-profile',
        id: 'demo-pslf-profile-1',
        attributes: { isInterested: true, signaturePresent: true },
        relationships: {
          employee: { data: { type: 'employee', id: 'demo-employee-1' } },
          pslfForms: { data: formRefs },
        },
      },
      {
        type: 'company',
        id: 'demo-company-1',
        attributes: {
          name: employerName,
          companyType: 'partner',
          isActive: true,
          programTitle: 'PSLF Program',
        },
        relationships: { pslfGroups: { data: [{ type: 'pslf-group', id: PSLF_GROUP_ID }] } },
      },
      {
        type: 'employee-addition',
        id: 'demo-emp-add-1',
        attributes: { processed: { merged: { fein: FEIN } } },
      },
      ...forms,
    ],
  };
}

function buildCompanyDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const { employerName } = identity;
  return {
    data: {
      type: 'company',
      id: 'demo-company-1',
      attributes: {
        name: employerName,
        companyType: 'partner',
        isActive: true,
        programTitle: 'PSLF Program',
      },
      relationships: { pslfGroups: { data: [{ type: 'pslf-group', id: PSLF_GROUP_ID }] } },
    },
    included: [
      {
        type: 'pslf-group',
        id: PSLF_GROUP_ID,
        attributes: {
          approverEmail: 'hr@example.com',
          approverName: 'Sarah Johnson',
          approverTitle: 'HR Director',
          employerCommonName: employerName,
          employerLegalName: `${employerName} Inc.`,
          employerFein: FEIN,
          employerIsNotForProfit: true,
          employerCity: 'Portland',
          employerState: 'OR',
          employerPostalCode: '97205',
        },
        relationships: { company: { data: { type: 'company', id: 'demo-company-1' } } },
      },
    ],
  };
}

// Inline PDF (data URI) for the demo form's attachment — a real, minimal PDF so
// the form/success route's preview renders client-side instead of fetching a
// signed-S3 url the network-free block would stop.
const DEMO_PSLF_FORM_PDF =
  'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iajw8L1R5cGUvQ2F0YWxvZy9QYWdlcyAyIDAgUj4+ZW5kb2JqCjIgMCBvYmo8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PmVuZG9iagozIDAgb2JqPDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgMzAwIDIwMF0vUmVzb3VyY2VzPDwvRm9udDw8L0YxIDQgMCBSPj4+Pi9Db250ZW50cyA1IDAgUj4+ZW5kb2JqCjQgMCBvYmo8PC9UeXBlL0ZvbnQvU3VidHlwZS9UeXBlMS9CYXNlRm9udC9IZWx2ZXRpY2E+PmVuZG9iago1IDAgb2JqPDwvTGVuZ3RoIDYyPj5zdHJlYW0KQlQgL0YxIDE0IFRmIDQwIDEyMCBUZCAoUFNMRiBGb3JtIC0gRGVtbyBEb2N1bWVudCkgVGogRVQKZW5kc3RyZWFtIGVuZG9iagp4cmVmCjAgNgowMDAwMDAwMDAwIDY1NTM1IGYgCnRyYWlsZXI8PC9Sb290IDEgMCBSL1NpemUgNj4+CnN0YXJ0eHJlZgowCiUlRU9G';

const attachmentIdFor = (formId: string) => `demo-pslf-attachment-${formId}`;

function buildPslfFormAttachment(formId: string): JsonApiResource {
  return {
    type: 'attachment',
    id: attachmentIdFor(formId),
    attributes: {
      filename: `pslf-form-${formId}.pdf`,
      contentType: 'application/pdf',
      signedUrl: DEMO_PSLF_FORM_PDF,
      tags: [`PslfForm:${formId}`],
    },
  };
}

interface PslfFormFixture {
  id: string;
  status: string;
  formType: 'currentEmployer' | 'priorEmployer';
  employerName?: string;
  employmentStartDate: string;
  employmentEndDate?: string;
  createdAt: string;
  borrowerCompletedAt: string;
  employerCompletedAt?: string;
  submittedAt?: string;
  employerRejectedAt?: string;
}

const SUBMITTED_CURRENT_FORM: PslfFormFixture = {
  id: 'demo-form-1',
  status: 'submitted',
  formType: 'currentEmployer',
  employmentStartDate: '2022-01-15',
  createdAt: '2026-05-16T09:00:00.000Z',
  borrowerCompletedAt: '2026-05-17',
  employerCompletedAt: '2026-05-21',
  submittedAt: '2026-05-22',
};

const MULTI_FORM_JOURNEY: PslfFormFixture[] = [
  SUBMITTED_CURRENT_FORM,
  {
    id: 'demo-form-2',
    status: 'submitted',
    formType: 'priorEmployer',
    employerName: 'City General Hospital',
    employmentStartDate: '2019-06-01',
    employmentEndDate: '2021-12-31',
    createdAt: '2026-05-04T09:00:00.000Z',
    borrowerCompletedAt: '2026-05-06',
    employerCompletedAt: '2026-05-11',
    submittedAt: '2026-05-12',
  },
  {
    id: 'demo-form-3',
    status: 'borrower_signed',
    formType: 'priorEmployer',
    employerName: 'Riverside Community College',
    employmentStartDate: '2017-08-15',
    employmentEndDate: '2019-05-31',
    createdAt: '2026-05-27T09:00:00.000Z',
    borrowerCompletedAt: '2026-05-29',
  },
];

const PSLF_FORMS_BY_SCENARIO = {
  'blank-slate': [],
  'borrower-signed': [
    {
      ...SUBMITTED_CURRENT_FORM,
      id: 'demo-form-borrower-signed',
      status: 'borrower_signed',
      employerCompletedAt: undefined,
      submittedAt: undefined,
    },
  ],
  'employer-signed': [
    {
      ...SUBMITTED_CURRENT_FORM,
      id: 'demo-form-employer-signed',
      status: 'awaiting_submit',
      submittedAt: undefined,
    },
  ],
  submitted: [SUBMITTED_CURRENT_FORM],
  'form-rejected': [
    {
      ...SUBMITTED_CURRENT_FORM,
      id: 'demo-form-rejected',
      status: 'rejected',
      submittedAt: undefined,
      employerRejectedAt: '2026-06-03',
    },
  ],
  'multi-form': MULTI_FORM_JOURNEY,
  'with-nslds': MULTI_FORM_JOURNEY,
  'full-journey': MULTI_FORM_JOURNEY,
} satisfies Record<PslfDemoScenarioId, PslfFormFixture[]>;

function buildPslfFormResource(
  fixture: PslfFormFixture,
  identity: DemoFixtureIdentity
): JsonApiResource {
  const { firstName, lastName } = identity;
  const employerName = fixture.employerName ?? identity.employerName;
  const employerSigned = fixture.status !== 'borrower_signed';
  return {
    type: 'pslf-form',
    id: fixture.id,
    attributes: {
      status: fixture.status,
      formType: fixture.formType,
      createdAt: fixture.createdAt,
      borrowerCompletedAt: fixture.borrowerCompletedAt,
      employerCompletedAt: fixture.employerCompletedAt ?? null,
      submittedAt: fixture.submittedAt ?? null,
      ...(fixture.employerRejectedAt ? { employerRejectedAt: fixture.employerRejectedAt } : {}),
      isNonPartner: false,
      borrowerFields: {
        'borrower-name': `${firstName} ${lastName}`,
        'borrower-email': demoEmail(identity),
        'borrower-employer-name': employerName,
        'borrower-employment-start-date': fixture.employmentStartDate,
        'borrower-employment-end-date': fixture.employmentEndDate ?? null,
        'borrower-employment-status': 'employed',
        'borrower-employment-continues': fixture.formType === 'currentEmployer',
        'borrower-employer-ein': FEIN,
      },
      employerFields: employerSigned
        ? {
            'employer-name': employerName,
            'employer-fein': FEIN,
            'employer-city': 'Portland',
            'employer-state': 'OR',
            'employer-zip': '97205',
            'employer-email': 'hr@example.com',
          }
        : {},
    },
    relationships: {
      pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
      attachments: { data: [{ type: 'attachment', id: attachmentIdFor(fixture.id) }] },
      employee: { data: { type: 'employee', id: 'demo-employee-1' } },
    },
  };
}

interface PslfSearchBody {
  from?: number;
  size?: number;
  query?: unknown;
}

interface PslfSearchRequestBody {
  'model-type'?: string;
  'search-body'?: PslfSearchBody;
  searchBody?: PslfSearchBody;
}

function valuesForField(value: unknown, field: string): unknown[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => valuesForField(item, field));
  }
  if (typeof value !== 'object' || value === null) {
    return [];
  }

  const record = value as Record<string, unknown>;
  const term = record['term'];
  const terms = record['terms'];
  const direct = [term, terms].flatMap((candidate) => {
    if (typeof candidate !== 'object' || candidate === null || !(field in candidate)) {
      return [];
    }
    const fieldValue = (candidate as Record<string, unknown>)[field];
    return Array.isArray(fieldValue) ? fieldValue : [fieldValue];
  });

  return [...direct, ...Object.values(record).flatMap((item) => valuesForField(item, field))];
}

function searchTokens(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(searchTokens);
  }
  if (typeof value !== 'object' || value === null) {
    return [];
  }

  const record = value as Record<string, unknown>;
  const multiMatch = record['multi_match'];
  const query =
    typeof multiMatch === 'object' && multiMatch !== null
      ? (multiMatch as Record<string, unknown>)['query']
      : undefined;
  return [
    ...(typeof query === 'string' ? [query] : []),
    ...Object.values(record).flatMap(searchTokens),
  ];
}

function pslfAdminHit(fixture: PslfFormFixture, identity: DemoFixtureIdentity) {
  const employerName = fixture.employerName ?? identity.employerName;
  const employerSigned = fixture.status !== 'borrower_signed';
  return {
    _index: 'pslf_forms',
    _id: fixture.id,
    _score: 1,
    _source: {
      borrowerEmail: demoEmail(identity),
      borrowerName: `${identity.firstName} ${identity.lastName}`,
      borrowerSignDate: fixture.borrowerCompletedAt,
      companyId: 'demo-tas-company-1',
      employerClosed: '',
      employerEmail: 'hr@example.com',
      employerName,
      employerSignDate: employerSigned ? (fixture.employerCompletedAt ?? '') : '',
      formAwaitingSignature: fixture.status === 'borrower_signed',
      formFaxVerified: 'verified',
      formId: fixture.id,
      formPreApproved: false,
      formPrintAndMail: false,
      formStatus: fixture.status,
      formNonPartner: false,
      formType: fixture.formType,
      payrollId: 'DEMO-1001',
      profileId: 'demo-pslf-profile-1',
      createdAt: fixture.createdAt,
      updatedAt: fixture.submittedAt ?? fixture.borrowerCompletedAt,
    },
  };
}

function buildPslfAdminSearchResponse(
  body: unknown,
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
) {
  const request = body as PslfSearchRequestBody | undefined;
  const searchBody = request?.['search-body'] ?? request?.searchBody;
  const statusValues = valuesForField(searchBody?.query, 'pslf_form_status_keyword').filter(
    (value): value is string => typeof value === 'string'
  );
  const formTypeValues = valuesForField(searchBody?.query, 'pslf_form_type_keyword').filter(
    (value): value is PslfFormFixture['formType'] =>
      value === 'currentEmployer' || value === 'priorEmployer'
  );
  const groupValues = valuesForField(
    searchBody?.query,
    'pslf_form_employer_group_id_keyword'
  ).filter((value): value is string => typeof value === 'string');
  const tokens = searchTokens(searchBody?.query).map((token) => token.toLowerCase());

  const filtered = PSLF_FORMS_BY_SCENARIO[scenarioId].filter((fixture) => {
    if (statusValues.length && !statusValues.includes(fixture.status)) {
      return false;
    }
    if (formTypeValues.length && !formTypeValues.includes(fixture.formType)) {
      return false;
    }
    if (groupValues.length && !groupValues.includes(PSLF_GROUP_ID)) {
      return false;
    }
    if (tokens.length) {
      const searchable = [
        identity.firstName,
        identity.lastName,
        demoEmail(identity),
        fixture.employerName ?? identity.employerName,
        'DEMO-1001',
      ]
        .join(' ')
        .toLowerCase();
      if (!tokens.some((token) => searchable.includes(token))) {
        return false;
      }
    }
    return true;
  });

  const from = Math.max(0, searchBody?.from ?? 0);
  const size = Math.max(0, searchBody?.size ?? 25);
  const hits = filtered.slice(from, from + size).map((fixture) => pslfAdminHit(fixture, identity));
  return {
    took: 0,
    timedOut: false,
    hits: { total: { value: filtered.length, relation: 'eq' }, maxScore: 1, hits },
    aggregations: {},
  };
}

// The newest submitted form's 'YYYY-MM-DD' date, as tio-user-profile reads pslf_form.submitted_at.
export function demoPslfLatestSubmittedAt(scenarioId: PslfDemoScenarioId) {
  const dates = PSLF_FORMS_BY_SCENARIO[scenarioId].flatMap((form) =>
    form.submittedAt ? [form.submittedAt] : []
  );
  return dates.sort().at(-1)?.slice(0, 10) ?? null;
}

function buildPslfFormResources(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): JsonApiResource[] {
  return PSLF_FORMS_BY_SCENARIO[scenarioId].map((fixture) =>
    buildPslfFormResource(fixture, identity)
  );
}

function buildPslfFormCollection(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): JsonApiDoc {
  const forms = buildPslfFormResources(scenarioId, identity);
  return {
    data: forms,
    ...(forms.length
      ? {
          included: [
            ...forms.map((form) => buildPslfFormAttachment(form.id)),
            {
              type: 'employee',
              id: 'demo-employee-1',
              attributes: {
                firstName: identity.firstName,
                lastName: identity.lastName,
                email: demoEmail(identity),
              },
            },
          ],
        }
      : {}),
    meta: { page: { total: forms.length } },
  };
}

// The form/success route reads a SINGLE pslf-form by id (findRecord, include:
// attachments). The id comes from a dashboard link or the just-submitted form's
// client id, so echo it — mirroring the collection form + its attachment.
function buildPslfFormSingle(
  url: string,
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): JsonApiDoc {
  const id = /\/pslf-forms\/([^/?]+)/.exec(url)?.[1] ?? 'demo-form-1';
  const fixture =
    PSLF_FORMS_BY_SCENARIO[scenarioId].find((candidate) => candidate.id === id) ??
    ({ ...SUBMITTED_CURRENT_FORM, id } satisfies PslfFormFixture);
  return {
    data: buildPslfFormResource(fixture, identity),
    included: [
      buildPslfFormAttachment(fixture.id),
      {
        type: 'employee',
        id: 'demo-employee-1',
        attributes: {
          firstName: identity.firstName,
          lastName: identity.lastName,
          email: demoEmail(identity),
        },
      },
    ],
  };
}

// A non-empty profile prevents the dashboard from attempting a blocked create.
const ANSWER_PROFILE_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'answer-profile',
      id: 'demo-answer-profile-1',
      attributes: {
        consolidatedLoansAt: null,
        changedRepaymentPlanAt: null,
        recertifiedIncomeAt: null,
      },
    },
  ],
};

// My Documents reads meta.page.total even for an empty collection.
const EMPTY_OBSERVABLE_DOCUMENTS: JsonApiDoc = { data: [], meta: { page: { total: 0 } } };

const isSingleResource = (segment: string) => (url: string) =>
  new RegExp(`/${segment}/[^/?]+(?:\\?|$)`).test(url);
const isPslfFormSingle = isSingleResource('pslf-forms');

/**
 * Build raw-fetch fixtures for an authored PSLF journey.
 * @param scenarioId selects the form state
 * @param identity supplies borrower and employer values
 * @returns ordered fetch matchers for PSLF builder requests
 */
export function buildPslfFetchMatchers(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity
): FetchMatcher[] {
  return [
    // pslf-form: the single read (form/success route, findRecord) is matched
    // FIRST so it gets a single-resource body; the collection query (dashboard
    // filter[pslf-profile]; drawer filter[employee-id]) is the fallback.
    {
      urlMatches: isPslfFormSingle,
      method: 'GET',
      json: (url: string) => buildPslfFormSingle(url, scenarioId, identity),
    },
    {
      urlMatches: (url) => url.includes('/pslf-forms') && !isPslfFormSingle(url),
      method: 'GET',
      json: buildPslfFormCollection(scenarioId, identity),
    },
    // Shared "My Documents" widget — coherent empty page.
    { urlIncludes: '/observable-documents', method: 'GET', json: EMPTY_OBSERVABLE_DOCUMENTS },
    // answer-profile findOrCreate (dash-recommendations, on render): a non-empty
    // query result short-circuits the createRecord (mutations are blocked in demo).
    { urlIncludes: '/answer-profiles', method: 'GET', json: ANSWER_PROFILE_COLLECTION },
  ];
}

/**
 * Build request-handler fixtures for an authored PSLF journey.
 * @param scenarioId selects the form state
 * @param identity supplies borrower and employer values
 * @param options enables role-restricted admin surfaces
 * @returns ordered operation matchers for legacy store requests
 */
export function buildPslfMatchers(
  scenarioId: PslfDemoScenarioId,
  identity: DemoFixtureIdentity,
  options: { includeAdminSearch?: boolean } = {}
): MatcherFixture[] {
  return [
    ...(options.includeAdminSearch
      ? [
          {
            matcher: {
              kind: 'builder' as const,
              method: 'POST',
              urlMatches: matchesSearchEndpoint,
              bodyMatches: (body: unknown) =>
                (body as PslfSearchRequestBody | undefined)?.['model-type'] === 'PslfForm',
            },
            contentFor: (body: unknown) => buildPslfAdminSearchResponse(body, scenarioId, identity),
          },
        ]
      : []),
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'employee' },
      docs: [buildEmployeeDoc(scenarioId, identity)],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'company' },
      docs: [buildCompanyDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'pslf-form' },
      docs: [buildPslfFormCollection(scenarioId, identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'answer-profile' },
      docs: [ANSWER_PROFILE_COLLECTION],
    },
  ];
}
